<template>
  <div>
    <div class="flex items-center space-x-4">
      <h2>Editar Mascota</h2>
    </div>

    <div v-if="!loading" class="card mt-2">
      <form v-if="petTypes" @submit.prevent="save">
        <div class="form-grid">
          <div class="form-field">
            <label for="pet_type">Animal</label>
            <custom-select
              id="pet_type"
              v-model="selectedType"
              :options="getPetTypeOptions()"
              disabled
            />
            <validation-errors :errors="errorFor('pet_type')"></validation-errors>
          </div>

          <div class="form-field">
            <label for="name">Nombre</label>
            <input id="name" type="text" v-model="form.name" />
            <validation-errors :errors="errorFor('name')"></validation-errors>
          </div>

          <div class="form-field">
            <label for="gender">Sexo</label>
            <custom-select
              id="gender"
              v-model="form.gender"
              :options="[
                { value: null, label: 'Seleccionar' },
                { value: 'Hembra', label: 'Hembra' },
                { value: 'Macho', label: 'Macho' },
              ]"
              @change="form.body_composition_id = null"
            />
            <validation-errors :errors="errorFor('gender')"></validation-errors>
          </div>

          <div class="form-field">
            <label for="birthdate">Fecha de nacimiento</label>
            <custom-date-input
              id="birthdate"
              type="date"
              v-model="form.birthdate"
            />
            <validation-errors
              :errors="errorFor('birthdate')"
            ></validation-errors>
          </div>

          <div class="form-field">
            <label for="breed">Raza</label>
            <custom-input-select
              id="breed"
              v-model="form.breed_id"
              :options="getBreedOptions()"
              :disabled="breeds === null"
            />
            <validation-errors :errors="errorFor('breed')"></validation-errors>
          </div>

          <div v-if="isOtroBreed" class="form-field">
            <label for="custom_breed">Especifica la raza</label>
            <input
              id="custom_breed"
              type="text"
              v-model="form.custom_breed"
              maxlength="120"
            />
            <validation-errors :errors="errorFor('custom_breed')"></validation-errors>
          </div>
        </div>

        <primary-button type="submit" class="mt-10 mx-0" :loading="loadingForm"
          >Guardar
        </primary-button>
      </form>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import validationErrors from "@/mixins/validationErrors";
import { is422 } from "@/utils/response";
export default {
  name: "Address Form",

  data() {
    return {
      selectedType: null,
      petTypes: null,
      form: {
        name: "",
        gender: null,
        birthdate: "",
        breed_id: "",
        custom_breed: "",
      },
      loading: false,
      loadingForm: false,
    };
  },

  computed: {
    breeds() {
      return this.selectedType
        ? this.petTypes.filter((type) => type.id === this.selectedType)[0]
            .breeds
        : null;
    },

    selectedBreed() {
      if (!this.breeds || !this.form.breed_id) return null;
      return this.breeds.find((b) => b.id === this.form.breed_id) || null;
    },

    isOtroBreed() {
      return this.selectedBreed && this.selectedBreed.name === "Otro";
    },
  },

  watch: {
    "form.breed_id"() {
      if (!this.isOtroBreed) {
        this.form.custom_breed = "";
      }
    },
  },

  created() {
    this.$store.commit("setSelectedTab", "pets");

    this.loadPet();
    this.loadPetTypes();
  },

  mixins: [validationErrors],

  methods: {
    async loadPetTypes() {
      this.loading = true;

      try {
        this.petTypes = await api.resources.getPetTypes();
      } catch (error) {}

      this.loading = false;
    },

    async loadPet() {
      this.loading = true;

      try {
        let pet = await api.pets.getById(this.$route.params.id);
        this.selectedType = pet.breed.pet_type_id;

        delete pet.pet_detail;
        delete pet.breed;

        this.form = await JSON.parse(JSON.stringify(pet));
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    getPetTypeOptions() {
      const options = [{ value: null, label: "Seleccionar", disabled: false }];

      this.petTypes.forEach((petType) => {
        options.push({
          value: petType.id,
          label: petType.name,
        });
      });

      return options;
    },

    getBreedOptions() {
      if (!this.breeds) {
        return [];
      }

      const options = [{ value: null, label: "Seleccionar", disabled: false }];

      this.breeds.forEach((breed) => {
        options.push({
          value: breed.id,
          label: breed.name,
        });
      });

      return options;
    },

    save() {
      if (!this.validateForm()) {
        this.$store.dispatch(
          "errorSnack",
          "Favor de llenar todos los campos correctamente."
        );
        return;
      }

      this.updatePet();
    },

    validateForm() {
      this.errors = {};

      if (!this.selectedType) {
        this.errors["pet_type"] = ["Campo requerido"];
      }

      if (!this.form.breed_id) {
        this.errors["breed"] = ["Campo requerido"];
      }

      if (this.isOtroBreed && !(this.form.custom_breed || "").trim()) {
        this.errors["custom_breed"] = ["Especifica la raza"];
      }

      if (!this.form.name) {
        this.errors["name"] = ["Campo requerido"];
      }

      if (!this.form.gender) {
        this.errors["gender"] = ["Campo requerido"];
      }

      if (!this.form.birthdate) {
        this.errors["birthdate"] = ["Campo requerido"];
      }

      const datePattern = /^(\d{4})\-(\d{2})\-(\d{2})$/;
      const match = this.form.birthdate.match(datePattern);

      if (!match) {
        this.errors["birthdate"] = ["Formato inválido. Utilice DD/MM/AAAA."];
      }

      return Object.keys(this.errors).length === 0;
    },

    async updatePet() {
      this.loadingForm = true;

      try {
        const response = await api.pets.update(
          this.$route.params.id,
          this.form
        );
        this.$store.dispatch("successSnack", response.message);
        this.$router.push({ name: 'petsDetail', params: { id: this.$route.params.id } });
      } catch (error) {
        if (is422(error)) {
          this.errors = error.response && error.response.data.errors;
        }
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingForm = false;
    },
  },
};
</script>